'use client'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import useEmblaCarousel from 'embla-carousel-react'

import { cn } from '@/utilities/ui'

export type ProjectCarouselItem = {
  id: number
  title: string
  imageUrl: string
  imageAlt?: string | null
  href?: string | null
}

const IMG_W = 700
const IMG_H = 400

/** Same timing constants as WhySectionProjectSlider */
const AUTOPLAY_SCROLL_MS = 6000
const AUTOPLAY_PAUSE_MS = 3000
/** Arrow-button click animation */
const TRANSITION_MS = 450

function visibleSlidesForViewportWidth(vw: number): number {
  if (vw < 640) return 1
  if (vw < 1280) return 2
  return 3
}

function CardLink({ href, children }: { href?: string | null; children: React.ReactNode }) {
  const raw = typeof href === 'string' ? href.trim() : ''
  if (!raw) {
    return (
      <div className="flex min-w-0 flex-col items-center gap-2" draggable={false}>
        {children}
      </div>
    )
  }
  const internal = raw.startsWith('/')
  if (internal) {
    return (
      <Link
        className="flex min-w-0 flex-col items-center gap-2 text-inherit no-underline"
        draggable={false}
        href={raw}
      >
        {children}
      </Link>
    )
  }
  return (
    <a
      className="flex min-w-0 flex-col items-center gap-2 text-inherit no-underline"
      draggable={false}
      href={raw}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  )
}

export const ProjectCarouselClient: React.FC<{
  heading: string
  items: ProjectCarouselItem[]
  hideHeading?: boolean
  className?: string
}> = ({ heading, items, hideHeading = false, className }) => {
  // ─── Embla (handles drag + pointer capture reliably) ───────────────────────
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false, // Disabled native loop to simplify custom CSS transition
    startIndex: items.length > 0 ? items.length * 20 : 0, // Start in the middle so user can drag left or right
    align: 'start',
    duration: TRANSITION_MS,   // used by arrow-button clicks
    slidesToScroll: 1,
  })

  // ─── Slide geometry (measured from Embla's root node) ─────────────────────
  const [slideWidth, setSlideWidth] = useState(0)
  const [gapPx, setGapPx] = useState(20)
  const [visibleCount, setVisibleCount] = useState(2)

  useLayoutEffect(() => {
    if (!emblaApi) return
    const root = emblaApi.rootNode()

    const measure = () => {
      const trackW = root.getBoundingClientRect().width
      const vw = window.innerWidth
      const vis = visibleSlidesForViewportWidth(vw)
      const g = 20
      const sw = trackW > 0 ? Math.max(0, (trackW - g * (vis - 1)) / vis) : 0
      setSlideWidth(sw)
      setGapPx(g)
      setVisibleCount(vis)
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(root)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [emblaApi])

  const stepPx = slideWidth + gapPx

  // Autoplay always runs — no hover-pause

  // ─── Reduce motion ─────────────────────────────────────────────────────────
  const [reduceMotion, setReduceMotion] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  // ─── Custom slow autoplay (CSS transition on Embla's container node) ────────
  //
  // Strategy:
  //   1. Directly write `transition` + `transform` on Embla's container node
  //      using the same slow cubic-bezier as WhySectionProjectSlider.
  //   2. After the animation finishes, INSTANTLY sync Embla's internal snap
  //      index via scrollTo(nextIndex, true) — Embla repositions without
  //      animating, so no visible jump occurs.
  //
  // Embla continues to own pointer events / drag / button clicks normally.
  // ──────────────────────────────────────────────────────────────────────────
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!emblaApi || reduceMotion || stepPx <= 0 || items.length <= 1) return

    let active = true

    function clear() {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current)
        autoplayTimerRef.current = null
      }
    }

    function scheduleNext() {
      if (!active) return
      autoplayTimerRef.current = setTimeout(scroll, AUTOPLAY_PAUSE_MS)
    }

    function scroll() {
      if (!active || !emblaApi) return

      const container = emblaApi.containerNode()

      // Read current X from live computed style (works even if Embla or a
      // previous CSS animation already moved the container)
      const matrix = new DOMMatrix(getComputedStyle(container).transform)
      const currentX = matrix.m41 // translateX component in px

      // Apply the same slow deceleration as WhySectionProjectSlider
      container.style.transition = `transform ${AUTOPLAY_SCROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
      container.style.transform = `translate3d(${currentX - stepPx}px, 0, 0)`

      autoplayTimerRef.current = setTimeout(() => {
        if (!active || !emblaApi) return

        // ── Sync Embla's internal index after the animation ──
        // 1. Kill the transition so the snap is invisible
        container.style.transition = 'none'

        // 2. Instantly move Embla to the next snap (true = jump, no Embla animation)
        emblaApi.scrollNext(true)

        scheduleNext()
      }, AUTOPLAY_SCROLL_MS)
    }

    const onPointerDown = () => {
      active = false
      clear()
      const container = emblaApi.containerNode()
      container.style.transition = 'none'
    }

    const onSettle = () => {
      active = true
      scheduleNext()
    }

    emblaApi.on('pointerDown', onPointerDown)
    emblaApi.on('settle', onSettle)

    scheduleNext()

    return () => {
      active = false
      clear()
      emblaApi.off('pointerDown', onPointerDown)
      emblaApi.off('settle', onSettle)
      // Stop any in-progress CSS transition cleanly
      if (emblaApi) {
        const container = emblaApi.containerNode()
        container.style.transition = 'none'
      }
    }
  }, [emblaApi, reduceMotion, stepPx, items.length])

  // Cancel autoplay when user interacts via arrow buttons
  const handleScrollPrev = useCallback(() => {
    if (!emblaApi) return
    // Kill CSS autoplay transition before Embla takes over
    const container = emblaApi.containerNode()
    container.style.transition = 'none'
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current)
      autoplayTimerRef.current = null
    }
    emblaApi.scrollPrev()
  }, [emblaApi])

  const handleScrollNext = useCallback(() => {
    if (!emblaApi) return
    const container = emblaApi.containerNode()
    container.style.transition = 'none'
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current)
      autoplayTimerRef.current = null
    }
    emblaApi.scrollNext()
  }, [emblaApi])

  // ─── Heading split ─────────────────────────────────────────────────────────
  const [headingLine1, headingLine2] = useMemo(() => {
    const t = heading.trim()
    const i = t.search(/\s/)
    if (i === -1) return [t, ''] as const
    return [
      t.slice(0, i),
      t.slice(i + 1).replace(/\s+/g, ' ').trim(),
    ] as const
  }, [heading])

  // ─── Slide item width (CSS) ────────────────────────────────────────────────
  const slideItemStyle = useMemo((): React.CSSProperties => {
    const v = Math.max(1, visibleCount)
    const g = gapPx
    if (slideWidth > 0) return { width: slideWidth, flexShrink: 0 }
    return { width: `calc((100% - ${g * (v - 1)}px) / ${v})`, flexShrink: 0 }
  }, [slideWidth, gapPx, visibleCount])

  // ─── Duplicated Items for Infinite Scroll without Embla Loop ───────────────
  const displayItems = useMemo(() => {
    if (items.length <= 1) return items
    const repeatCount = 40 // Duplicate enough times to last virtually forever
    const result: (ProjectCarouselItem & { originalIndex: number })[] = []
    for (let i = 0; i < repeatCount; i++) {
      items.forEach((item, index) => {
        result.push({ ...item, originalIndex: index })
      })
    }
    return result
  }, [items])

  if (items.length === 0) return null

  const showArrows = items.length > 1

  return (
    <section className={cn('mx-auto w-full max-w-none relative', className)}>
      {!hideHeading && (
        <div className="container-fluid mb-4 px-4 md:px-7">
          <h2 className="text-[32px] font-normal leading-tight tracking-tight text-[#222]">
            <span className="block">{headingLine1}</span>
            {headingLine2 ? <span className="block">{headingLine2}</span> : null}
          </h2>
          <div aria-hidden className="mt-1 h-2.5 w-[320px] max-w-[60%] bg-[#1595a7]" />
        </div>
      )}

      <div className="container-fluid relative px-[10px] lg:px-0 pb-0">
        {/* Embla viewport — overflow:hidden required by Embla */}
        <div
          ref={emblaRef}
          className="min-h-0 w-full min-w-0 overflow-hidden cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: 'pan-y' }}

        >
          {/* Embla container — Embla owns transform; autoplay overlays via CSS */}
          <div
            className="flex will-change-transform"
            style={{ gap: gapPx }}
          >
            {displayItems.map((item, i) => (
              <div
                key={`${item.id}-${i}`}
                className="box-border min-w-0"
                style={slideItemStyle}
              >
                <CardLink href={item.href}>
                  <div className="relative mx-auto h-[400px] w-full min-w-0 overflow-hidden bg-[#e8e8e8] md:h-[400px]">
                    <Image
                      alt={item.imageAlt || item.title}
                      className="h-full w-full object-cover"
                      draggable={false}
                      height={IMG_H}
                      onDragStart={(e) => e.preventDefault()}
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      src={item.imageUrl}
                      width={IMG_W}
                    />
                  </div>
                  <p
                    className="mt-[20px] mx-auto max-w-[280px] px-1 text-center text-[16px] font-medium leading-[16px]"
                    style={{
                      color: 'rgb(29,29,27)',
                      fontFamily: 'var(--font-outfit-family), sans-serif',
                    }}
                  >
                    {item.title}
                  </p>
                </CardLink>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showArrows && (
        <>
          <button
            type="button"
            aria-label="Previous"
            onClick={handleScrollPrev}
            className="absolute left-[5px] top-1/2 z-10 -translate-y-1/2 flex h-[40px] w-[40px] items-center justify-center transition-opacity hover:opacity-70"
            style={{ color: 'var(--arrow-normal-color, hsla(0,0%,93%,.9))' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
              fill="currentColor"
              width={24}
              height={24}
              aria-hidden
            >
              <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
            </svg>
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={handleScrollNext}
            className="absolute right-[5px] top-1/2 z-10 -translate-y-1/2 flex h-[40px] w-[40px] items-center justify-center transition-opacity hover:opacity-70"
            style={{ color: 'var(--arrow-normal-color, hsla(0,0%,93%,.9))' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
              fill="currentColor"
              width={24}
              height={24}
              aria-hidden
            >
              <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L243.2 256 73.9 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
            </svg>
          </button>
        </>
      )}
    </section>
  )
}
